import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { Box, Button, Collapse, Grid2, MenuItem, Popover, Typography } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { applyFilters, getKpiCount, getKpiInsight } from '../../store/slices/kpiReportingSlice';
import { getCustomRanges, highlightSelectedDateRange } from '../../utils/DateUtility';

import { AppDispatch } from '../../store/store';
import { DateRangePicker } from 'react-date-range';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GradientFilledButton from '../styled/GradientBorderedButton'
import { KPIContext } from '../../contexts/kpiContext';
import { KPI_REPORTING_FILTERS } from '../../constants/KPIReportingFiltersConstants';
import NoBorderSelect from '../styled/NoBorderSelect';
import SiteNamesSelect from '../shared/SiteNamesSelect';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';

function KPIFilters() {
    const dispatch = useDispatch<AppDispatch>();

    const kpiContext = useContext(KPIContext);
    const [showDetailedFilters, setShowDetailedFilters] = useState(true);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const filtersRef = useRef<HTMLDivElement>(null);
    const [filtersDimension, setFiltersDimension] = useState<{ width: number, height: number }>({ width: 0, height: 0 })

    useEffect(() => {
        const handleResize = () => {
            if (filtersRef.current) {
                const { clientWidth, clientHeight } = filtersRef.current;
                setFiltersDimension({ width: clientWidth, height: clientHeight + 100 })
            }
        };

        const resizeObserver = new ResizeObserver(handleResize);
        const currentRef = filtersRef.current;
        if (currentRef) {
            resizeObserver.observe(currentRef);
            handleResize();
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (currentRef)
                resizeObserver.unobserve(currentRef);
            resizeObserver.disconnect();
        };
    }, [])

    const getClipPathForFilters = () => {
        const angle = 40;
        return `M ${angle} 0 H ${filtersDimension.width} L ${filtersDimension.width - angle} ${filtersDimension.height} H 0 Z`
    }

    const open = Boolean(anchorEl);
    const id = open ? 'date-range-pop-over' : undefined;

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const [dateRange, setDateRange] = useState([
        {
            startDate: kpiContext?.state.filters.startDate,
            endDate: kpiContext?.state.filters.endDate,
            key: 'selection',
        }
    ])

    const staticDateRanges = getCustomRanges(dateRange[0].startDate!.getFullYear())
    const handleSelect = (ranges: any) => {
        setDateRange([ranges.selection]);
        kpiContext?.updateFilters({
            startDate: ranges.selection.startDate,
            endDate: ranges.selection.endDate,
            businessEntity: kpiContext.state.filters!.businessEntity,
            operatingAsset: kpiContext.state.filters!.operatingAsset,
            siteName: kpiContext.state.filters!.siteName,
            selectedKpiName: kpiContext.state.filters!.selectedKpiName
        });
    }

    const handleSiteNameChanged = (values: any) => {
        const previous = kpiContext?.state.filters;
        kpiContext?.updateFilters({
            startDate: previous!.startDate,
            endDate: previous!.endDate,
            businessEntity: previous!.businessEntity,
            operatingAsset: previous!.operatingAsset,
            siteName: values.length > 0 ? [...values] : [],
            selectedKpiName: previous!.selectedKpiName
        })
    }

    const handleOperatingAssetChanged = (event: any) => {
        const previous = kpiContext?.state.filters;
        kpiContext?.updateFilters({
            startDate: previous!.startDate,
            endDate: previous!.endDate,
            businessEntity: previous!.businessEntity,
            operatingAsset: event.target.value as string,
            siteName: previous!.siteName,
            selectedKpiName: previous!.selectedKpiName
        })
    }

    const handleBusinessEntityChanged = (event: any) => {
        const previous = kpiContext?.state.filters;

        let sites = [] as string[];
        if (event.target.value === 'Subsidiaries') {
            sites = [...KPI_REPORTING_FILTERS.SITE_NAMES_ASR];
        }
        else if (event.target.value === 'Joint Ventures') {
            sites = [...KPI_REPORTING_FILTERS.SITE_NAMES_JV];
        } else {
            sites = [...KPI_REPORTING_FILTERS.SITE_NAMES_ASR, ...KPI_REPORTING_FILTERS.SITE_NAMES_JV]
        }
        kpiContext?.updateFilters({
            startDate: previous!.startDate,
            endDate: previous!.endDate,
            businessEntity: event.target.value as string,
            operatingAsset: previous!.operatingAsset,
            siteName: [...sites],
            selectedKpiName: previous!.selectedKpiName
        })
    }

    const handleApplyFilters = () => {
        dispatch(applyFilters({
            ...kpiContext?.state.filters,
            kpiName: kpiContext?.state.filters.selectedKpiName
        }))
        dispatch(getKpiCount({ ...kpiContext?.state.filters }))
        if (kpiContext?.state.showKpiDetails === true)
            dispatch(getKpiInsight({
                ...kpiContext?.state.filters,
                kpiName: kpiContext?.state.filters.selectedKpiName
            }))
    }

    return (
        <>
            <Grid2 id={'kpi_filters'}
                ref={filtersRef}
                sx={{
                    width: { xs: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    paddingX: 10,
                    paddingY: 2,
                    gap: 1,
                    background: '#03F0FF1A',
                    clipPath: `path('${getClipPathForFilters()}')`
                }}>
                <Box sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    alignItems: 'center',
                    justifyItems: 'center'
                }}>
                    <Typography sx={{ fontWeight: 600 }}>
                        Show Data from
                    </Typography>
                    <Button variant='outlined'
                        onClick={handleClick}
                        endIcon={(anchorEl) ? <ExpandLessIcon fontSize='large' /> : <ExpandMoreIcon fontSize='large' />}
                        sx={{
                            fontWeight: '400 !important',
                            color: 'var(--text-dark) !important',
                            background: '#FFFFFF !important',
                            display: 'flex !important',
                            justifyContent: 'space-between !important',
                            outline: 'none',
                            border: 'none',
                            borderRadius: '1vw',
                            paddingX: '1vw',
                            paddingY: '0.75vh',
                        }}>
                        <Typography variant='body1' textTransform={'capitalize'}>
                            {`${format(dateRange[0].startDate!, "LLLL do yyyy")} - ${format(dateRange[0].endDate!, "LLLL do yyyy")}`}
                        </Typography>
                    </Button>

                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        hideBackdrop={false}
                        closeAfterTransition={false}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        sx={{
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <Grid2 sx={{
                            width: '30vw',
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'center',
                            '& .rdrInputRanges': {
                                display: 'none',
                            },
                            '& .rdrDayNumber': {
                                fontWeight: 700,
                            },
                            '& .rdrDateRangePickerWrapper *, & .rdrDateRangePickerWrapper input':{
                                fontSize: '0.75rem !important',
                            },
                            '& .rdrDateRangePickerWrapper': {
                                width: '100%'
                            },
                            '& .rdrDefinedRangesWrapper':{
                                width: '33%',
                            },
                            '& .rdrDateRangeWrapper': {
                                width: '66%',
                            },
                            '& .rdrMonthsHorizontal, & .rdrMonth': {
                                width: '100%',
                                height: '50%'
                            },
                            '& .rdrStaticRanges > button' : {
                                paddingY: '0.5vh'
                            },
                            '& .rdrMonthAndYearPickers select' : {
                                position: 'relative'
                            },
                            '& .rdrMonthAndYearWrapper button': {
                                '& i': {
                                    borderWidth: '0.3vw'
                                }
                            }
                        }}>
                            <DateRangePicker
                                displayMode='dateRange'
                                maxDate={new Date()}
                                showPreview={true}
                                direction="horizontal"
                                ranges={dateRange}
                                rangeColors={['#3ED4DC', 'blue']}
                                onChange={handleSelect}
                                staticRanges={highlightSelectedDateRange(dateRange[0].startDate!, dateRange[0].endDate!, staticDateRanges)}
                            ></DateRangePicker>
                        </Grid2>
                    </Popover>
                    <GradientFilledButton variant='outlined'
                        onClick={handleApplyFilters}
                        sx={{
                            borderRadius: '1vw !important',
                            padding: '0.5vh 1vw',
                            border: '0 !important',
                            boxShadow: 'none !important',
                            color: 'var(--text-light) !important',
                            background: 'linear-gradient(95.6deg, #5A4EB4 -121.37%, #00A9B1 18.52%, #2E79B3 88.99%) !important'
                        }}>
                        <Typography fontWeight={'700'}>Apply All Filters</Typography>
                    </GradientFilledButton>
                </Box>
                <Collapse
                    in={showDetailedFilters}
                    sx={{
                        width: { md: '100%' },
                    }}>
                    <Grid2 sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        placeContent: 'center',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{ fontWeight: 600 }}>
                                Business Entity
                            </Typography>
                            <NoBorderSelect variant='outlined'
                                defaultValue={kpiContext?.state.filters.businessEntity}
                                onChange={handleBusinessEntityChanged}
                                IconComponent={ExpandMoreIcon}
                                sx={{
                                    fontWeight: 400,
                                    textTransform: 'none',
                                    background: 'var(--text-light)'
                                }}>
                                <MenuItem value={'all'}>All</MenuItem>
                                {KPI_REPORTING_FILTERS.BUSINESS_ENTITIES.map((be: string, index: number) => {
                                    return <MenuItem key={`be-${index}`} value={be}>{be}</MenuItem>
                                })}
                            </NoBorderSelect>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{ fontWeight: 600 }}>
                                Operating Asset
                            </Typography>
                            <NoBorderSelect variant='outlined'
                                defaultValue={kpiContext?.state.filters.operatingAsset}
                                onChange={handleOperatingAssetChanged}
                                IconComponent={ExpandMoreIcon}
                                sx={{
                                    fontWeight: 400,
                                    textTransform: 'none',
                                    background: 'var(--text-light)'
                                }}>
                                <MenuItem value={'all'}>All</MenuItem>
                                {KPI_REPORTING_FILTERS.OPERATING_ASSETS.map((oa: string, index: number) => {
                                    return <MenuItem key={`oa-${index}`} value={oa}>{oa}</MenuItem>
                                })}
                            </NoBorderSelect>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{ fontWeight: 600 }}>
                                Site Name
                            </Typography>
                            <SiteNamesSelect
                                defaultValues={[...kpiContext?.state.filters.siteName!]}
                                siteNamesChangedFn={handleSiteNameChanged}></SiteNamesSelect>
                        </Box>
                    </Grid2>
                </Collapse>
                <Button variant='text'
                    sx={{
                        alignSelf: 'center',
                        color: '#00A9B1',
                        fontWeight: 700,
                        textDecoration: 'underline'
                    }}
                    onClick={() => setShowDetailedFilters(previous => !previous)}>
                    {showDetailedFilters ? 'Hide' : 'Expand'} Detailed Filters
                </Button>
            </Grid2>
        </>
    )
}

export default KPIFilters